import { z } from 'zod';
import { coreIds } from './ids.js';
import {
  ASSOCIATION_KIND_AGENT_LABEL,
  associationDataString,
  type AgentAssociation,
  type AgentAssociationRef,
} from './agent-association.types.js';

/**
 * Labels describe the *kind of work* a run is: a bug fix, a refactor, a docs
 * pass. Where a {@link AgentGroup} is a bucket you put runs in by hand, a
 * label is normally applied for you — the prompt classifier that titles a new
 * agent also picks its labels off the organization's catalog.
 *
 * The storage split matches groups exactly, and for the same reason: the
 * `agent_labels` table owns the label's identity (name + the description the
 * classifier reads), and membership rides on `agent_associations` under the
 * `agent_label` kind, so a label shows up as a chip, filters the list, and
 * needs no second hydration path.
 */

/** The `data` key a membership mirrors the label's display name under. */
export const AGENT_LABEL_NAME_KEY = 'name';

/** The role every label membership carries — see {@link AGENT_GROUP_MEMBER_ROLE}. */
export const AGENT_LABEL_MEMBER_ROLE = 'member' as const;

export const AGENT_LABEL_NAME_MAX_LENGTH = 40;

/** How many labels one classification may apply to a run. */
export const MAX_LABELS_PER_CLASSIFICATION = 3;

/**
 * How many labels a launch may carry ({@link AgentConfig.labelIds}). Higher
 * than the classifier's cap because a person picking labels by hand knows
 * what they mean, where the model is guessing; it exists to bound the launch
 * payload, not to second-guess the choice. A run wearing ten labels is
 * already telling you nothing, so nothing is lost at the ceiling.
 */
export const MAX_LAUNCH_LABELS = 10;

/**
 * How many labels a single classification may invent. The catalog is a
 * vocabulary, not a free-text field: if every run could mint its own labels
 * the list would end up with a hundred near-synonyms and filtering by one of
 * them would tell you nothing. One is enough for a genuinely new kind of work
 * to enter the catalog and be reused by later runs.
 */
export const MAX_GENERATED_LABELS_PER_CLASSIFICATION = 1;

export const AgentLabelNameSchema = z
  .string()
  .trim()
  .min(1, 'Label name is required')
  .max(
    AGENT_LABEL_NAME_MAX_LENGTH,
    `Label name must be ${AGENT_LABEL_NAME_MAX_LENGTH} characters or fewer`,
  );

/**
 * Where a label came from.
 * - `builtin`: seeded from {@link DEFAULT_AGENT_LABELS} when the organization
 *   first reads its catalog;
 * - `user`: someone typed it;
 * - `generated`: the classifier proposed it because nothing in the catalog fit.
 *
 * Kept because the three want different treatment over time: a `generated`
 * label nobody reuses is the first thing worth pruning, and a `builtin` one is
 * the last.
 */
export const AgentLabelSourceSchema = z.enum(['builtin', 'user', 'generated']);
export type AgentLabelSource = z.infer<typeof AgentLabelSourceSchema>;

export const AgentLabelSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    /**
     * What the label means. Written for the classifier as much as for people:
     * it is the only thing telling the model when to pick this label over a
     * neighbouring one.
     */
    description: z.string().nullable(),
    source: AgentLabelSourceSchema,
    /** Number of agents currently carrying the label. */
    memberCount: z.number(),
    createdAt: z.number(),
  })
  .meta({ id: 'AgentLabel' });
export type AgentLabel = z.infer<typeof AgentLabelSchema>;

export const ListAgentLabelsResponseSchema = z
  .object({ labels: z.array(AgentLabelSchema) })
  .meta({ id: 'ListAgentLabelsResponse' });
export type ListAgentLabelsResponse = z.infer<typeof ListAgentLabelsResponseSchema>;

export const CreateAgentLabelRequestSchema = z
  .object({
    name: AgentLabelNameSchema,
    description: z.string().max(200).optional(),
    /** Agents to label immediately, the same one-step gesture groups allow. */
    agentIds: z.array(z.string()).optional(),
  })
  .meta({ id: 'CreateAgentLabelRequest' });
export type CreateAgentLabelRequest = z.infer<typeof CreateAgentLabelRequestSchema>;

export const UpdateAgentLabelRequestSchema = z
  .object({
    name: AgentLabelNameSchema.optional(),
    description: z.string().max(200).nullable().optional(),
  })
  .meta({ id: 'UpdateAgentLabelRequest' });
export type UpdateAgentLabelRequest = z.infer<typeof UpdateAgentLabelRequestSchema>;

export const AgentLabelResponseSchema = z
  .object({ label: AgentLabelSchema })
  .meta({ id: 'AgentLabelResponse' });
export type AgentLabelResponse = z.infer<typeof AgentLabelResponseSchema>;

export const AgentLabelMembersRequestSchema = z
  .object({ agentIds: z.array(z.string()).min(1) })
  .meta({ id: 'AgentLabelMembersRequest' });
export type AgentLabelMembersRequest = z.infer<typeof AgentLabelMembersRequestSchema>;

export const AgentLabelParamsSchema = z.object({ labelId: coreIds.schema('agentLabel') });
export type AgentLabelParams = z.infer<typeof AgentLabelParamsSchema>;

/**
 * The starter vocabulary, seeded into an organization the first time it reads
 * its catalog. These are the kinds of work that show up in every codebase, so
 * a fresh organization gets a classifier that can label usefully on its first
 * run rather than inventing a vocabulary one prompt at a time.
 *
 * The descriptions are prompt material: they are what the model reads to
 * choose between neighbours (a `Refactor` that adds no behaviour vs a
 * `Feature` that does), so they are phrased as boundaries, not blurbs.
 */
export const DEFAULT_AGENT_LABELS: readonly { name: string; description: string }[] = [
  { name: 'Feature', description: 'Builds new user-facing capability that did not exist before.' },
  { name: 'Bug fix', description: 'Diagnoses and repairs behaviour that is already broken.' },
  {
    name: 'Refactor',
    description: 'Restructures existing code without changing what it does.',
  },
  { name: 'Tests', description: 'Adds, repairs, or extends automated tests.' },
  { name: 'Docs', description: 'Writes or revises documentation, guides, or written copy.' },
  {
    name: 'Infra',
    description: 'Build, CI, deployment, dependencies, tooling, and environment work.',
  },
  { name: 'Performance', description: 'Makes something measurably faster, smaller, or cheaper.' },
  {
    name: 'Security',
    description: 'Access control, secrets, vulnerabilities, and hardening work.',
  },
  {
    name: 'Review',
    description: 'Reads and critiques a change someone else wrote, rather than writing one.',
  },
  {
    name: 'Migration',
    description: 'Moves data, schema, or callers from one shape to another.',
  },
  { name: 'Design', description: 'Visual, layout, and interaction work on the interface.' },
  {
    name: 'Research',
    description:
      'Investigation, reproduction, or a spike that answers a question rather than shipping a change.',
  },
];

/** A label an agent carries, as read off its association refs. */
export interface AgentLabelRef {
  id: string;
  name: string;
}

/** Display name of a label membership, falling back to its id. */
export function agentLabelRefName(ref: AgentAssociationRef): string {
  return associationDataString(ref, AGENT_LABEL_NAME_KEY) ?? ref.externalId;
}

/** The labels on an agent, in the order they were recorded. */
export function agentLabelRefs(agent: {
  associations?: AgentAssociation[] | null;
}): AgentLabelRef[] {
  return (agent.associations ?? [])
    .filter((a) => a.kind === ASSOCIATION_KIND_AGENT_LABEL)
    .map((a) => ({ id: a.externalId, name: agentLabelRefName(a) }));
}

/** Whether the agent carries `labelId`. */
export function agentHasLabel(
  agent: { associations?: AgentAssociation[] | null },
  labelId: string,
): boolean {
  return (agent.associations ?? []).some(
    (a) => a.kind === ASSOCIATION_KIND_AGENT_LABEL && a.externalId === labelId,
  );
}

/**
 * The key two label names are considered the same under.
 *
 * Case alone is not enough. The failure this exists for is a catalog that
 * grows `Code review`, `Review code`, `code-review`, and `Code reviews` —
 * four labels for one idea, each matching a quarter of the runs, so filtering
 * by any of them tells you nothing. Folding punctuation, word order, and
 * plurals collapses all four onto the first one that was created.
 *
 * The trade is a false collision between two names that share their words in
 * a different order and genuinely mean different things. Over a vocabulary of
 * a few dozen short labels that is a much rarer, and much cheaper, mistake
 * than the duplicates it prevents: the collision is visible the moment
 * someone types the name (they get told which label already covers it),
 * whereas the duplicates are invisible until the list is useless.
 */
export function labelMatchKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(singularize)
    .sort()
    .join(' ');
}

/**
 * Crude de-pluralization, enough for `Tests`/`Test` and `Bug fixes`/`Bug fix`
 * and deliberately not a stemmer — a label vocabulary is short, human-written,
 * and does not need one. The `-es` case is restricted to the endings that
 * actually take it (`fixes`, `passes`, `patches`), so `cases` still folds to
 * `case` rather than `cas`. Nothing is stripped when the result would be under
 * three characters: that is where a plural fold starts turning unrelated short
 * words into each other (`ops` → `op`).
 */
function singularize(word: string): string {
  const esStem = /(x|s|z|ch|sh)es$/.test(word) ? word.slice(0, -2) : null;
  if (esStem && esStem.length >= 3) return esStem;
  if (word.endsWith('s')) {
    const stem = word.slice(0, -1);
    if (stem.length >= 3) return stem;
  }
  return word;
}

/** Whether two label names name the same thing. See {@link labelMatchKey}. */
export function labelNamesMatch(a: string, b: string): boolean {
  return labelMatchKey(a) === labelMatchKey(b);
}

/**
 * Normalize the label names a classification returned: trim, drop empties and
 * over-long ones, fold duplicates that differ only in case, and cap the
 * count. The model is asked for well-formed names but is not trusted to
 * return them — this is the one place that contract is enforced, so both the
 * apply path and its tests agree on what a valid set looks like.
 */
export function normalizeClassifiedLabels(
  names: unknown,
  max: number = MAX_LABELS_PER_CLASSIFICATION,
): string[] {
  if (!Array.isArray(names)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    if (typeof raw !== 'string') continue;
    const name = raw.trim();
    if (!name || name.length > AGENT_LABEL_NAME_MAX_LENGTH) continue;
    const key = labelMatchKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (out.length >= max) break;
  }
  return out;
}
